import { lazy, memo, Suspense, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState, type Ref, type RefObject } from "react";
import Archive from "lucide-react/dist/esm/icons/archive.mjs";
import ArrowLeft from "lucide-react/dist/esm/icons/arrow-left.mjs";
import BookOpen from "lucide-react/dist/esm/icons/book-open.mjs";
import Ellipsis from "lucide-react/dist/esm/icons/ellipsis.mjs";
import Settings2 from "lucide-react/dist/esm/icons/settings-2.mjs";
import Star from "lucide-react/dist/esm/icons/star.mjs";
import Trash2 from "lucide-react/dist/esm/icons/trash-2.mjs";
import type { ReaderItem, ReaderPreferences, ReadingCopyFigure } from "../types";
import { readingCopyBody, sourceActionUrl, validReadingCopy } from "../readingCopy";
import { ArticleDates } from "./ArticleDates";
import { ArtworkImage } from "./ArtworkImage";
import { Brand } from "./Brand";

const MarkdownArticle = lazy(() => import("./MarkdownArticle"));
const PROGRESS_UPDATE_INTERVAL_MS = 250;
const FOCUSABLE_DISCLOSURE_ENTRY = "button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

export interface ReaderPaneHandle {
  focusArticleHeading: () => boolean;
  checkpointProgress: () => number | undefined;
}

interface ReaderPaneProps {
  item?: ReaderItem;
  loading: boolean;
  refreshing?: boolean;
  error?: string;
  focus: boolean;
  online: boolean;
  deleteBusy?: boolean;
  stateBusy?: boolean;
  favoriteBusy?: boolean;
  backLabel: string;
  preferences: ReaderPreferences;
  annotationsCount: number;
  onBack: () => void;
  onFocus: () => void;
  onFavorite: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onAnnotations: () => void;
  onPreferences: (value: ReaderPreferences) => void;
  onProgress: (progress: number) => void;
  onRetry?: () => void;
  readerHandleRef?: Ref<ReaderPaneHandle>;
  initialScrollTop?: number;
  onInitialScrollRestored?: () => void;
}

export function ReaderPane({
  item,
  loading,
  refreshing = false,
  error,
  focus,
  online,
  deleteBusy = false,
  stateBusy = false,
  favoriteBusy = false,
  backLabel,
  preferences,
  annotationsCount,
  onBack,
  onFocus,
  onFavorite,
  onArchive,
  onDelete,
  onAnnotations,
  onPreferences,
  onProgress,
  onRetry,
  readerHandleRef,
  initialScrollTop,
  onInitialScrollRestored,
}: ReaderPaneProps) {
  const scroller = useRef<HTMLDivElement>(null);
  const articleHeading = useRef<HTMLHeadingElement>(null);
  const progressRef = useRef(item?.reading_progress || 0);
  const committedProgressRef = useRef(item?.reading_progress || 0);
  const restorationRef = useRef({ restored: false, engaged: false, waiting: false });
  const onInitialScrollRestoredRef = useRef(onInitialScrollRestored);
  onInitialScrollRestoredRef.current = onInitialScrollRestored;
  const onProgressRef = useRef(onProgress);
  const checkpointProgressRef = useRef<() => number | undefined>(() => undefined);
  const progressDisplay = useRef<HTMLDivElement>(null);
  const progressElement = useRef<HTMLProgressElement>(null);
  const progressText = useRef<HTMLElement>(null);
  const settingsAnchor = useRef<HTMLDivElement>(null);
  const settingsTrigger = useRef<HTMLButtonElement>(null);
  const settingsPanel = useRef<HTMLDivElement>(null);
  const moreAnchor = useRef<HTMLDivElement>(null);
  const moreTrigger = useRef<HTMLButtonElement>(null);
  const morePanel = useRef<HTMLDivElement>(null);
  const settingsId = useId();
  const moreId = useId();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [renderedItemId, setRenderedItemId] = useState("");
  const readingCopy = validReadingCopy(item?.reading_copy) ? item.reading_copy : undefined;
  const title = readingCopy?.title || item?.title || "Untitled read";
  const author = readingCopy ? readingCopy.authors.join(", ") : item?.byline || item?.author_name || item?.author?.name;
  const publication = readingCopy ? readingCopy.publication : item?.site_name;
  const publisherUrl = sourceActionUrl(readingCopy ? readingCopy.publisher_url : item?.url);
  const outboundUrl = sourceActionUrl(readingCopy?.outbound_url);
  const markdown = readingCopyBody(item?.markdown || "", readingCopy);
  const readingUnavailable = Boolean(item?.reader_status && item.reader_status !== "readable");
  const sourceActions = outboundUrl || publisherUrl ? <div className="article-source-actions">
    {outboundUrl ? <a href={outboundUrl} target="_blank" rel="noopener noreferrer">Open linked content ↗</a> : null}
    {publisherUrl && publisherUrl !== outboundUrl ? <a href={publisherUrl} target="_blank" rel="noopener noreferrer">Open publisher page ↗</a> : null}
  </div> : null;

  onProgressRef.current = onProgress;

  const handleArticleRendered = useCallback(() => {
    if (item?.id) setRenderedItemId(item.id);
  }, [item?.id]);

  const updateProgressDisplay = useCallback((value: number) => {
    const percent = Math.round(value * 100);
    progressDisplay.current?.setAttribute("aria-label", `${percent}% read`);
    if (progressElement.current) progressElement.current.value = percent;
    if (progressText.current) progressText.current.textContent = `${percent}%`;
  }, []);

  const closeSettings = useCallback(() => setSettingsOpen(false), []);
  const closeMore = useCallback(() => setMoreOpen(false), []);
  useDisclosureLifecycle(settingsOpen, settingsAnchor, settingsPanel, settingsTrigger, closeSettings);
  useDisclosureLifecycle(moreOpen, moreAnchor, morePanel, moreTrigger, closeMore);

  useImperativeHandle(readerHandleRef, () => ({
    focusArticleHeading: () => {
      const heading = articleHeading.current;
      if (!heading) return false;
      heading.focus({ preventScroll: true });
      return document.activeElement === heading;
    },
    checkpointProgress: () => checkpointProgressRef.current(),
  }), []);

  useEffect(() => {
    const state = { restored: false, engaged: false, waiting: (item?.reading_progress || 0) > 0 || initialScrollTop !== undefined };
    restorationRef.current = state;
    const element = scroller.current;
    const engage = () => { state.engaged = true; state.waiting = false; };
    const events = ["pointerdown", "touchstart", "wheel", "keydown"];
    events.forEach((event) => element?.addEventListener(event, engage, { passive: true }));
    return () => events.forEach((event) => element?.removeEventListener(event, engage));
  }, [item?.id]);

  useEffect(() => {
    const value = item?.reading_progress || 0;
    progressRef.current = value;
    committedProgressRef.current = value;
    updateProgressDisplay(value);
  }, [item?.id, item?.reading_progress, updateProgressDisplay]);

  useEffect(() => {
    const element = scroller.current;
    const target = item?.reading_progress || 0;
    const state = restorationRef.current;
    const contentReady = item?.reader_status && item.reader_status !== "readable" ? true : renderedItemId === item?.id;
    if (!element || !item || state.restored || state.engaged || !contentReady) return;
    if (target <= 0 && initialScrollTop === undefined) { state.waiting = false; return; }
    state.waiting = true;
    let frame = 0;
    let stopped = false;
    let fontsReady = !document.fonts;
    const pendingImages = new Set(Array.from(element.querySelectorAll("img")).filter((image) => !image.complete));
    let imageTimer = 0;
    const restore = () => {
      frame = 0;
      if (stopped || !fontsReady || pendingImages.size) return;
      const scrollable = element.scrollHeight - element.clientHeight;
      if (state.restored || state.engaged || (scrollable <= 1 && (target > 0 || (initialScrollTop ?? 0) > 0))) return;
      element.scrollTop = initialScrollTop ?? target * Math.max(0, scrollable);
      state.waiting = false;
      // A cached zero may precede the saved server progress. Accept that first
      // positive checkpoint until the user has interacted, then never jump on
      // background revalidation. An update's exact pixel position takes priority.
      state.restored = initialScrollTop !== undefined || target > 0;
      if (initialScrollTop !== undefined) onInitialScrollRestoredRef.current?.();
      observer.disconnect();
      resizeObserver?.disconnect();
    };
    const observer = new MutationObserver(() => {
      if (!frame) frame = requestAnimationFrame(restore);
    });
    const resizeObserver = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(() => {
      if (!frame) frame = requestAnimationFrame(restore);
    });
    const imageReady = (event: Event) => {
      pendingImages.delete(event.currentTarget as HTMLImageElement);
      if (!pendingImages.size && !frame) frame = requestAnimationFrame(restore);
    };
    const images = [...pendingImages];
    images.forEach((image) => {
      image.addEventListener("load", imageReady);
      image.addEventListener("error", imageReady);
      // Percentage bookmarks need the article's full height, including images
      // below the viewport. Ordinary reads still load those images lazily.
      image.loading = "eager";
    });
    if (images.length) imageTimer = window.setTimeout(() => {
      // A stalled image must not indefinitely prevent opening a saved position.
      pendingImages.clear();
      if (!frame) frame = requestAnimationFrame(restore);
    }, 3000);
    observer.observe(element, { childList: true, subtree: true });
    resizeObserver?.observe(element);
    if (element.firstElementChild) resizeObserver?.observe(element.firstElementChild);
    if (fontsReady) frame = requestAnimationFrame(restore);
    else void document.fonts.ready.then(() => {
      fontsReady = true;
      if (!stopped && !frame) frame = requestAnimationFrame(restore);
    });
    return () => {
      stopped = true;
      window.clearTimeout(imageTimer);
      images.forEach((image) => {
        image.removeEventListener("load", imageReady);
        image.removeEventListener("error", imageReady);
      });
      observer.disconnect();
      resizeObserver?.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [item?.id, item?.reading_progress, renderedItemId, initialScrollTop]);

  useEffect(() => {
    const element = scroller.current;
    if (!element || !item) return;
    const itemProgressCallback = onProgressRef.current;
    let frame = 0;
    let updateTimer = 0;

    const readProgress = () => {
      if (restorationRef.current.waiting) return progressRef.current;
      const scrollable = element.scrollHeight - element.clientHeight;
      if (scrollable <= 1) return progressRef.current;
      return Math.min(1, Math.max(0, element.scrollTop / scrollable));
    };
    const commitProgress = (updateVisibleProgress: boolean, callback = onProgressRef.current) => {
      if (updateTimer) {
        window.clearTimeout(updateTimer);
        updateTimer = 0;
      }
      const progress = progressRef.current;
      if (progress === committedProgressRef.current) return;
      committedProgressRef.current = progress;
      if (updateVisibleProgress) updateProgressDisplay(progress);
      callback(progress);
    };
    const scheduleProgressCommit = () => {
      if (updateTimer) return;
      updateTimer = window.setTimeout(() => {
        updateTimer = 0;
        commitProgress(true);
      }, PROGRESS_UPDATE_INTERVAL_MS);
    };
    const measureProgress = () => {
      frame = 0;
      progressRef.current = readProgress();
      if (progressRef.current === 1) {
        commitProgress(true);
      } else {
        scheduleProgressCommit();
      }
    };
    const checkpointProgress = () => {
      if (frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }
      progressRef.current = readProgress();
      commitProgress(true);
      return progressRef.current;
    };
    checkpointProgressRef.current = checkpointProgress;
    const listener = () => {
      if (!restorationRef.current.waiting && element.scrollTop > 0) restorationRef.current.engaged = true;
      if (!frame) frame = requestAnimationFrame(measureProgress);
    };
    element.addEventListener("scroll", listener, { passive: true });
    return () => {
      if (checkpointProgressRef.current === checkpointProgress) checkpointProgressRef.current = () => undefined;
      element.removeEventListener("scroll", listener);
      if (frame) cancelAnimationFrame(frame);
      progressRef.current = readProgress();
      commitProgress(false, itemProgressCallback);
    };
  }, [item?.id, updateProgressDisplay]);

  const progress = Math.round((item?.reading_progress || 0) * 100);
  const readerClasses = useMemo(() => [
    "reader-pane",
    focus ? "reader-pane--focus" : "",
    `reader-pane--size-${preferences.typeSize}`,
    `reader-pane--width-${preferences.width}`,
    `reader-pane--theme-${preferences.theme}`,
  ].filter(Boolean).join(" "), [focus, preferences]);
  const runMoreAction = useCallback((action: () => void) => {
    setMoreOpen(false);
    moreTrigger.current?.focus();
    action();
  }, []);

  if (!item && !loading && !error) {
    return (
      <section className="reader-empty">
        <BookOpen aria-hidden="true" />
        <h2>Choose something to read</h2>
        <p>Your saved article will open in a quiet, focused reader.</p>
      </section>
    );
  }

  return (
    <section className={readerClasses} aria-label={item ? title : "Reader"}>
      <header className="reader-toolbar">
        {focus ? <Brand compact /> : null}
        <button type="button" onClick={onBack} className="reader-toolbar__back" aria-label={`Back to ${backLabel}`}><ArrowLeft aria-hidden="true" /><span>Back</span></button>
        <div className="reader-toolbar__actions">
          <div className="reader-settings-anchor" ref={settingsAnchor}>
            <button ref={settingsTrigger} type="button" onClick={() => { setSettingsOpen((value) => !value); setMoreOpen(false); }} aria-expanded={settingsOpen} aria-controls={settingsId} aria-label="Reading settings"><Settings2 aria-hidden="true" /><span>Reading settings</span></button>
            {settingsOpen ? <ReadingSettings id={settingsId} panelRef={settingsPanel} preferences={preferences} onChange={onPreferences} /> : null}
          </div>
          <div className="reader-more-anchor" ref={moreAnchor}>
            <button ref={moreTrigger} type="button" onClick={() => { setMoreOpen((value) => !value); setSettingsOpen(false); }} disabled={!item} aria-expanded={moreOpen} aria-controls={moreId} aria-label={annotationsCount ? `More · ${annotationsCount}` : "More"}><Ellipsis aria-hidden="true" /><span>More{annotationsCount ? ` · ${annotationsCount}` : ""}</span></button>
            {moreOpen ? (
              <div ref={morePanel} id={moreId} className="reader-more-menu" role="group" aria-label="Reader actions">
                <button type="button" onClick={() => runMoreAction(onFocus)}><BookOpen aria-hidden="true" />{focus ? "Exit focus reader" : "Focus reader"}</button>
                <button type="button" disabled={stateBusy || favoriteBusy || deleteBusy} onClick={() => runMoreAction(onFavorite)}><Star fill={item?.is_favorite ? "currentColor" : "none"} aria-hidden="true" />{favoriteBusy ? "Updating…" : item?.is_favorite ? "Unfavorite" : "Favorite"}</button>
                <button type="button" onClick={() => runMoreAction(onAnnotations)}><Ellipsis aria-hidden="true" />Highlights & notes{annotationsCount ? ` · ${annotationsCount}` : ""}</button>
                <button type="button" onClick={() => runMoreAction(onArchive)} disabled={stateBusy || favoriteBusy || deleteBusy}><Archive aria-hidden="true" />{stateBusy ? item?.status === "archive" ? "Restoring…" : "Archiving…" : item?.status === "archive" ? "Restore to Library" : "Archive"}</button>
                <button className="reader-action--delete" type="button" onClick={() => runMoreAction(onDelete)} disabled={!online || deleteBusy || stateBusy || favoriteBusy}><Trash2 aria-hidden="true" />{deleteBusy ? "Deleting…" : "Delete"}</button>
              </div>
            ) : null}
          </div>
        </div>
      </header>
      <div className="reader-scroll" ref={scroller}>
        {loading && !item ? <ReaderLoading /> : null}
        {error ? <div className="margin-note reader-error inline-recovery" role="alert"><p>{error}</p>{onRetry ? <button type="button" onClick={onRetry}>Retry</button> : null}</div> : null}
        {item ? (
          <article className="article-sheet">
            <div className="reader-progress" ref={progressDisplay} aria-label={`${progress}% read`}><progress ref={progressElement} max={100} value={progress} aria-hidden="true" /><b ref={progressText}>{progress}%</b></div>
            <header className="article-header">
              <p>{(item.content_type || "article").toUpperCase()}</p>
              <h1 ref={articleHeading} tabIndex={-1}>{title}</h1>
              {readingCopy?.subtitle ? <p role="doc-subtitle" className="article-subtitle">{readingCopy.subtitle}</p> : null}
              <div>{[author, publication, item.estimated_minutes ? `${item.estimated_minutes} min read` : ""].filter(Boolean).join(" · ")}</div>
              <ArticleDates item={item} />
              {!readingUnavailable ? sourceActions : null}
              {item.tags?.length ? <ul className="article-tags" aria-label="Tags">{item.tags.map((tag) => <li key={tag.id || tag.name}>{tag.name}</li>)}</ul> : null}
            </header>
            {readingCopy && readingCopy.method !== "model" ? <p className="reading-copy-note">{readingCopy.method === "saved_source" ? "Showing the saved source. Some page clutter may remain." : "Simplified formatting from the saved source."}</p> : null}
            {item.artwork_state === "ready" && item.artwork ? <ArtworkImage artwork={item.artwork} className="article-artwork" loading="lazy" ownerItemId={item.id} /> : null}
            {readingUnavailable ? (
              <div className="margin-note inline-recovery" role="status">
                <p>{readerAvailabilityMessage(item.reader_status || "")}</p>
                <div className="reader-availability__actions">
                  {sourceActions}
                  {onRetry && !["unavailable", "superseded"].includes(item.reader_status || "") ? (
                    <button type="button" disabled={!online || refreshing} onClick={onRetry}>{refreshing ? "Checking…" : online ? "Check again" : "Check when online"}</button>
                  ) : null}
                </div>
              </div>
            ) : <ReaderArticleBody
              itemId={item.id}
              markdown={markdown || "This saved page has no readable text yet."}
              figures={readingCopy?.figures}
              onRendered={handleArticleRendered}
            />}
          </article>
        ) : null}
      </div>
      {!focus && item ? (
        <footer className="reader-bottom-actions reader-desktop-actions">
          <button type="button" onClick={onFocus}><BookOpen aria-hidden="true" />Focus reader</button>
          <button type="button" disabled={stateBusy || favoriteBusy || deleteBusy} onClick={onFavorite}><Star aria-hidden="true" />{favoriteBusy ? "Updating…" : item.is_favorite ? "Unfavorite" : "Favorite"}</button>
          <button type="button" disabled={stateBusy || favoriteBusy || deleteBusy} onClick={onArchive}><Archive aria-hidden="true" />{stateBusy ? item.status === "archive" ? "Restoring…" : "Archiving…" : item.status === "archive" ? "Restore to Library" : "Archive"}</button>
          <button className="reader-action--delete" type="button" disabled={!online || deleteBusy || stateBusy || favoriteBusy} onClick={onDelete}><Trash2 aria-hidden="true" />{deleteBusy ? "Deleting…" : "Delete"}</button>
          <button type="button" onClick={onAnnotations}><Ellipsis aria-hidden="true" />Highlights & notes</button>
        </footer>
      ) : null}
    </section>
  );
}

type ReaderArticleBodyProps = {
  itemId: string;
  markdown: string;
  figures?: ReadingCopyFigure[];
  onRendered: () => void;
};

const ReaderArticleBody = memo(function ReaderArticleBody({ markdown, figures, onRendered }: ReaderArticleBodyProps) {
  return (
    <Suspense fallback={<ArticleBodyLoading label="Typesetting article…" />}>
      <MarkdownArticle markdown={markdown} figures={figures} onRendered={onRendered} />
    </Suspense>
  );
}, (previous, next) => (
  previous.itemId === next.itemId
  && previous.markdown === next.markdown
  && previous.figures === next.figures
));

function readerAvailabilityMessage(status: string): string {
  switch (status) {
    case "processing":
      return "This page is still being prepared. Your saved item and reading position are safe. Check again for the latest reading copy.";
    case "failed":
      return "Imprint could not prepare a reading copy. Your saved item is safe. If the original page is available, you can read it there. Check again to see whether a reading copy has become available.";
    case "review_needed":
      return "This reading copy needs review before it is ready. Your saved item is safe. You can read the original page if it is available.";
    case "unavailable":
      return "The original source is no longer available. This saved item remains in your library for reference.";
    case "superseded":
      return "This older save has been replaced by another copy in your library. Use Find to look for the current copy.";
    default:
      return "A reading copy is not available for this saved item. You can check again or read the original page if it is available.";
  }
}

function ReaderLoading() {
  return (
    <article className="article-sheet reader-loading-sheet" role="status">
      <span className="sr-only">Preparing reader…</span>
      <div className="loading-line loading-line--progress" aria-hidden="true" />
      <div className="loading-line loading-line--kicker" aria-hidden="true" />
      <div className="loading-line loading-line--headline" aria-hidden="true" />
      <div className="loading-line loading-line--headline loading-line--headline-short" aria-hidden="true" />
      <div className="loading-line loading-line--meta" aria-hidden="true" />
      <div className="reader-loading-rule" aria-hidden="true" />
      <ArticleBodyLoading announce={false} />
    </article>
  );
}

function ArticleBodyLoading({ label = "Preparing article text…", announce = true }: { label?: string; announce?: boolean }) {
  return (
    <div className="article-body-loading" role={announce ? "status" : undefined} aria-hidden={announce ? undefined : "true"}>
      {announce ? <span className="sr-only">{label}</span> : null}
      {["wide", "wide", "medium", "wide", "short", "wide"].map((width, index) => <span className={`loading-line loading-line--body loading-line--${width}`} aria-hidden="true" key={`${width}-${index}`} />)}
    </div>
  );
}

function ReadingSettings({ id, panelRef, preferences, onChange }: { id: string; panelRef: RefObject<HTMLDivElement | null>; preferences: ReaderPreferences; onChange: (value: ReaderPreferences) => void }) {
  return (
    <div ref={panelRef} id={id} className="reading-settings" role="group" aria-label="Reading settings">
      <SettingGroup label="Type size" values={["s", "m", "l"]} active={preferences.typeSize} onSelect={(typeSize) => onChange({ ...preferences, typeSize: typeSize as ReaderPreferences["typeSize"] })} />
      <SettingGroup label="Width" values={["narrow", "wide"]} active={preferences.width} onSelect={(width) => onChange({ ...preferences, width: width as ReaderPreferences["width"] })} />
      <SettingGroup label="Theme" values={["paper", "white"]} active={preferences.theme} onSelect={(theme) => onChange({ ...preferences, theme: theme as ReaderPreferences["theme"] })} />
    </div>
  );
}

function useDisclosureLifecycle(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  triggerRef: RefObject<HTMLElement | null>,
  onClose: () => void,
) {
  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector<HTMLElement>(FOCUSABLE_DISCLOSURE_ENTRY)?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
      triggerRef.current?.focus();
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node) || anchorRef.current?.contains(target)) return;
      onClose();
      const interactiveTarget = target instanceof Element ? target.closest(FOCUSABLE_DISCLOSURE_ENTRY) : null;
      if (!interactiveTarget) window.setTimeout(() => triggerRef.current?.focus(), 0);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [anchorRef, onClose, open, panelRef, triggerRef]);
}

function SettingGroup({ label, values, active, onSelect }: { label: string; values: string[]; active: string; onSelect: (value: string) => void }) {
  return (
    <fieldset>
      <legend>{label}</legend>
      <div className="setting-options">
        {values.map((value) => <button key={value} type="button" className={value === active ? "active" : ""} aria-label={`${label}: ${value}`} aria-pressed={value === active} onClick={() => onSelect(value)}>{value}</button>)}
      </div>
    </fieldset>
  );
}
